// GitHub no escritório, sem token e sem internet: os eventos saem só dos comandos que os agentes
// rodaram (gh, git push) e do que esses comandos imprimiram, lidos dos transcripts.
//
// Aqui só há lógica pura: dado o comando e o resultado, diz o que aconteceu (PR aberto, PR
// mergeado, release, CI vermelho ou verde, push). Os adapters (claude, codex) chamam isto quando
// uma ferramenta termina e emitem uma Activity kind 'github'; o resultado em si nunca sai daqui,
// só o sinal estruturado (número do PR, ramo, tag).

export type GithubKind = 'pr-open' | 'pr-merge' | 'release' | 'ci-fail' | 'ci-pass' | 'push';

/** O que aconteceu no GitHub, extraído de uma chamada de ferramenta. */
export interface GithubSignal {
  kind: GithubKind;
  /** Repositório 'dono/nome', quando a saída traz. */
  repo?: string;
  /** Número do PR. */
  number?: number;
  branch?: string;
  /** Tag da release. */
  tag?: string;
  url?: string;
}

/** Prioridade ao escolher o evento principal quando um comando faz várias coisas. */
const KIND_ORDER: GithubKind[] = ['release', 'pr-merge', 'pr-open', 'ci-fail', 'ci-pass', 'push'];

/** Emoji de cada tipo, no feed e nos avisos. */
export const GITHUB_EMOJI: Record<GithubKind, string> = {
  'pr-open': '🎉',
  'pr-merge': '🎉',
  release: '🎉',
  'ci-fail': '🚨',
  'ci-pass': '✅',
  push: '⬆️',
};

/** Tipos que fazem festa na sala. */
export const GITHUB_PARTY: ReadonlySet<GithubKind> = new Set<GithubKind>(['pr-open', 'pr-merge', 'release']);

export function isGithubKind(value: unknown): value is GithubKind {
  return typeof value === 'string' && (KIND_ORDER as string[]).includes(value);
}

/** Texto curto em português do evento (sem o nome de quem fez): vai para Activity.text. */
export function githubText(signal: GithubSignal, project?: string): string {
  const em = project ? ` em ${project}` : '';
  const pr = signal.number !== undefined ? ` #${signal.number}` : '';
  const ramo = signal.branch ? ` (${signal.branch})` : '';
  switch (signal.kind) {
    case 'pr-open':
      return `${GITHUB_EMOJI['pr-open']} abriu o PR${pr}${em}`;
    case 'pr-merge':
      return `${GITHUB_EMOJI['pr-merge']} mergeou o PR${pr}${em}`;
    case 'release':
      return `${GITHUB_EMOJI.release} publicou a release${signal.tag ? ` ${signal.tag}` : ''}${em}`;
    case 'ci-fail':
      return `${GITHUB_EMOJI['ci-fail']} CI falhou${em}${ramo}`;
    case 'ci-pass':
      return `${GITHUB_EMOJI['ci-pass']} CI verde${em}${ramo}`;
    case 'push':
      return `${GITHUB_EMOJI.push} push${signal.branch ? ` em ${signal.branch}` : ''}${em}`;
  }
}

/** Faixa da festa: 'PR #12 mergeado!'. */
export function githubBanner(signal: GithubSignal): string | undefined {
  const pr = signal.number !== undefined ? ` #${signal.number}` : '';
  if (signal.kind === 'pr-open') return `PR${pr} aberto!`;
  if (signal.kind === 'pr-merge') return `PR${pr} mergeado!`;
  if (signal.kind === 'release') return `Release${signal.tag ? ` ${signal.tag}` : ''} publicada!`;
  return undefined;
}

// ------------------------------------------------------------------ linha de comando

/** Um comando `gh` ou `git push` achado numa linha de shell. */
export interface GithubCall {
  tool: 'gh' | 'git';
  /** 'pr create', 'pr merge', 'pr checks', 'run watch', 'run view', 'release create', 'push'. */
  verb: string;
  /** Argumentos depois do verbo (sem aspas). */
  args: string[];
}

const GH_VERBS = new Set(['pr create', 'pr merge', 'pr checks', 'run watch', 'run view', 'release create']);

/** Tira o corpo dos heredocs (`<<'EOF' … EOF`): o texto deles não é comando. */
function withoutHeredocs(command: string): string {
  return command.replace(/<<-?\s*(['"]?)([A-Za-z_][\w]*)\1[^\n]*\n[\s\S]*?\n[ \t]*\2(?=\n|$)/g, '');
}

/** Parte a linha em segmentos (`&&`, `||`, `;`, `|`, nova linha) respeitando as aspas. */
function segmentsOf(command: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quote = '';
  for (let i = 0; i < command.length; i++) {
    const ch = command[i] ?? '';
    if (quote) {
      cur += ch;
      if (ch === '\\' && quote === '"') cur += command[++i] ?? '';
      else if (ch === quote) quote = '';
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      cur += ch;
    } else if (ch === '\n' || ch === ';' || ch === '|' || ch === '&') {
      if ((ch === '|' || ch === '&') && command[i + 1] === ch) i++;
      // `2>&1` e `&>`: o & não separa nada
      if (ch === '&' && (command[i - 1] === '>' || command[i + 1] === '>')) {
        cur += ch;
        continue;
      }
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((s) => s.trim()).filter(Boolean);
}

/** Palavras de um segmento, sem as aspas. */
function wordsOf(segment: string): string[] {
  const out: string[] = [];
  const re = /"((?:\\.|[^"\\])*)"|'([^']*)'|(\S+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(segment))) out.push(m[1] ?? m[2] ?? m[3] ?? '');
  return out;
}

/** Acha as chamadas gh/git push de uma linha de shell (entra em `bash -c '…'`). */
export function githubCalls(command: string, depth = 0): GithubCall[] {
  const out: GithubCall[] = [];
  for (const segment of segmentsOf(withoutHeredocs(command))) {
    let words = wordsOf(segment);
    // VAR=valor gh …
    while (words.length && /^[A-Za-z_]\w*=/.test(words[0] ?? '')) words = words.slice(1);
    // sudo / time / env na frente
    while (words.length && ['sudo', 'time', 'env', 'command', 'exec'].includes(words[0] ?? '')) words = words.slice(1);
    const first = words[0] ?? '';
    if (/^(?:ba|z)?sh$/.test(first) && depth < 2) {
      const at = words.findIndex((w) => /^-[a-z]*c$/.test(w));
      if (at >= 0 && words[at + 1]) out.push(...githubCalls(words[at + 1] ?? '', depth + 1));
      continue;
    }
    if (first === 'gh') {
      // `-R dono/repo` e `--repo dono/repo` podem vir antes do verbo
      const rest: string[] = [];
      for (let i = 1; i < words.length; i++) {
        const w = words[i] ?? '';
        if ((w === '-R' || w === '--repo') && i + 1 < words.length) i++;
        else rest.push(w);
      }
      const verb = `${rest[0] ?? ''} ${rest[1] ?? ''}`;
      if (GH_VERBS.has(verb)) out.push({ tool: 'gh', verb, args: rest.slice(2) });
    } else if (first === 'git') {
      const rest: string[] = [];
      for (let i = 1; i < words.length; i++) {
        const w = words[i] ?? '';
        if ((w === '-C' || w === '-c') && i + 1 < words.length) i++;
        else rest.push(w);
      }
      if (rest[0] === 'push') out.push({ tool: 'git', verb: 'push', args: rest.slice(1) });
    }
  }
  return out;
}

/** O comando pode gerar evento? Filtro barato para o adapter só guardar o que importa. */
export function looksGithub(command: string): boolean {
  return /\bgh\b|\bgit\b/.test(command) && githubCalls(command).length > 0;
}

// ------------------------------------------------------------------ resultado

/**
 * Separa o texto de uma saída de shell do código de saída. Aceita o texto cru, o formato do Codex
 * (`Exit code: 1\nWall time: …\nOutput:\n…`), o JSON `{"output":…,"metadata":{"exit_code":1}}` e o
 * `Exit code 1` que o Claude Code escreve na frente quando o comando falha.
 */
export function shellResult(raw: string): { output: string; exitCode?: number } {
  const text = raw.trim();
  if (text.startsWith('{')) {
    try {
      const json = JSON.parse(text) as { output?: unknown; metadata?: { exit_code?: unknown } };
      if (typeof json.output === 'string') {
        const code = json.metadata?.exit_code;
        return { output: json.output, ...(typeof code === 'number' ? { exitCode: code } : {}) };
      }
    } catch {
      // não era JSON: segue como texto
    }
  }
  const codex = /^Exit code:\s*(-?\d+)\s*\n(?:Wall time:[^\n]*\n)?Output:\n?([\s\S]*)$/.exec(text);
  if (codex) return { output: codex[2] ?? '', exitCode: Number(codex[1]) };
  const claude = /^Exit code\s+(-?\d+)\s*\n?([\s\S]*)$/.exec(text);
  if (claude) return { output: claude[2] ?? '', exitCode: Number(claude[1]) };
  return { output: text };
}

const REPO = String.raw`([\w.-]+\/[\w.-]+)`;
const PULL_URL = new RegExp(String.raw`https?:\/\/github\.com\/${REPO}\/pull\/(\d+)`);
const RELEASE_URL = new RegExp(String.raw`https?:\/\/github\.com\/${REPO}\/releases\/tag\/([^\s)"']+)`);

/** Numa linha `X`/`fail` de `gh pr checks` (tabela, com ou sem tty) ou no resumo "N failing". */
function checksFailed(out: string): boolean {
  if (/^\S.*\t(?:fail|failure|cancel(?:led)?|timed_out)\b/im.test(out)) return true;
  if (/^X\s+\S/m.test(out)) return true;
  if (/Some checks were not successful/i.test(out)) return true;
  const failing = /\b([1-9]\d*)\s+(?:failing|failed)\b/i.exec(out);
  return Boolean(failing);
}

function checksPassed(out: string): boolean {
  if (/All checks were successful/i.test(out)) return true;
  // tabela sem tty: só linhas pass/skipping
  const rows = out.split('\n').filter((l) => /\t(?:pass|fail|pending|skipping|cancel)/i.test(l));
  return rows.length > 0 && rows.every((l) => /\t(?:pass|skipping)\b/i.test(l));
}

/** `gh run watch/view`: o cabeçalho "✓ …" ou "X …", ou "completed with 'failure'". */
function runResult(out: string): 'ci-fail' | 'ci-pass' | undefined {
  const done = /completed with '(\w+)'/i.exec(out);
  if (done) return /^(?:success|neutral|skipped)$/i.test(done[1] ?? '') ? 'ci-pass' : 'ci-fail';
  const head = /^([✓X✗])\s+\S.*·\s*\d+/m.exec(out);
  if (head) return head[1] === '✓' ? 'ci-pass' : 'ci-fail';
  return undefined;
}

function numberOf(args: string[]): number | undefined {
  for (const arg of args) {
    const url = /\/pull\/(\d+)/.exec(arg);
    if (url) return Number(url[1]);
    if (/^#?\d+$/.test(arg)) return Number(arg.replace('#', ''));
  }
  return undefined;
}

/** Ramo do último `-> ramo` que o git push imprimiu (ou o último argumento que não é opção). */
function pushedBranch(out: string, args: string[]): string | undefined {
  let branch: string | undefined;
  for (const m of out.matchAll(/^\s*(?:[ +*!=-]\s+)?(?:\S+\.\.\.?\S+|\[[^\]]+\])\s+\S+\s+->\s+(\S+)\s*$/gm)) branch = m[1];
  if (branch) return branch.replace(/^refs\/heads\//, '');
  const plain = args.filter((a) => !a.startsWith('-'));
  return plain[1]?.replace(/^refs\/heads\//, '').replace(/^\+/, '').split(':').pop();
}

function pushWorked(out: string, exitCode: number | undefined): boolean {
  if (exitCode !== undefined && exitCode !== 0) return false;
  if (/^\s*!\s+\[(?:rejected|remote rejected)/m.test(out) || /^(?:error|fatal):/m.test(out)) return false;
  if (/Everything up-to-date/i.test(out)) return false;
  return /->/.test(out) || /\bTo\s+\S*github\.com/.test(out);
}

/**
 * O que um comando fez no GitHub, a partir do comando e do que ele imprimiu. `failed` diz que a
 * ferramenta marcou erro (o Claude usa is_error). Devolve [] quando nada reconhecível aconteceu.
 */
export function detectCommand(command: string, result: string, failed = false): GithubSignal[] {
  const calls = githubCalls(command);
  if (!calls.length) return [];
  const { output, exitCode } = shellResult(result);
  const ok = !failed && (exitCode === undefined || exitCode === 0);
  const signals: GithubSignal[] = [];
  const add = (signal: GithubSignal) => {
    if (!signals.some((s) => s.kind === signal.kind)) signals.push(signal);
  };
  for (const call of calls) {
    switch (call.verb) {
      case 'pr create': {
        const m = PULL_URL.exec(output);
        if (m && ok) add({ kind: 'pr-open', repo: m[1], number: Number(m[2]), url: m[0] });
        break;
      }
      case 'pr merge': {
        if (!ok) break;
        // --auto só agenda o merge: ainda não aconteceu
        if (/will be automatically merged|auto-merge/i.test(output) || call.args.includes('--auto')) break;
        if (/not mergeable|cannot be merged|merge conflict|required status check|^X\s/im.test(output)) break;
        const merged = /(?:merged pull request|Pull request)\s+(?:([\w.-]+\/[\w.-]+))?#?(\d+)?/i.exec(output);
        const number = numberOf(call.args) ?? (merged?.[2] ? Number(merged[2]) : undefined);
        const url = PULL_URL.exec(output) ?? PULL_URL.exec(call.args.join(' '));
        add({ kind: 'pr-merge', ...(number !== undefined ? { number } : url ? { number: Number(url[2]) } : {}), ...(merged?.[1] ? { repo: merged[1] } : url ? { repo: url[1] } : {}) });
        break;
      }
      case 'release create': {
        const m = RELEASE_URL.exec(output);
        const tag = m?.[2] ?? call.args.find((a) => !a.startsWith('-'));
        // sem URL e com falha: nada foi publicado
        if (ok && (m || exitCode === 0 || (output.trim() === '' && tag))) add({ kind: 'release', ...(tag ? { tag } : {}), ...(m ? { repo: m[1], url: m[0] } : {}) });
        break;
      }
      case 'pr checks': {
        // exit 1 = há check falhando, 8 = pendente: a saída é que diz
        const number = numberOf(call.args);
        const branch = call.args.find((a) => !a.startsWith('-') && !/^#?\d+$/.test(a) && !a.includes('/pull/'));
        const extra = { ...(number !== undefined ? { number } : {}), ...(branch ? { branch } : {}) };
        if (checksFailed(output)) add({ kind: 'ci-fail', ...extra });
        else if (checksPassed(output)) add({ kind: 'ci-pass', ...extra });
        break;
      }
      case 'run watch':
      case 'run view': {
        const r = runResult(output);
        if (r) add({ kind: r });
        break;
      }
      case 'push': {
        if (!pushWorked(output, exitCode) || failed) break;
        const repo = /\bTo\s+(?:https?:\/\/|git@|ssh:\/\/git@)?github\.com[:/]([\w.-]+\/[\w.-]+?)(?:\.git)?\s*$/m.exec(output)?.[1];
        const branch = pushedBranch(output, call.args);
        add({ kind: 'push', ...(repo ? { repo } : {}), ...(branch ? { branch } : {}) });
        break;
      }
      default:
        break;
    }
  }
  return signals.sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind));
}

// ------------------------------------------------------------------ ferramentas mcp__github__*

/** `mcp__github__create_pull_request` (ou de um plugin: `mcp__plugin_github_github__…`) → nome da operação. */
export function githubMcpOp(tool: string): string | undefined {
  return /^mcp__[\w-]*github[\w-]*__([\w-]+)$/i.exec(tool)?.[1];
}

function parseJson(text: string): Record<string, unknown> {
  try {
    const v: unknown = JSON.parse(text.trim());
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

const asNum = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : undefined);
const asStr = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

/** O que uma ferramenta mcp__github__* fez, a partir da entrada e do resultado (JSON em texto). */
export function detectMcp(tool: string, input: Record<string, unknown>, result: string, failed = false): GithubSignal[] {
  const op = githubMcpOp(tool);
  if (!op || failed) return [];
  const data = parseJson(result);
  const repo = asStr(input.owner) && asStr(input.repo) ? `${String(input.owner)}/${String(input.repo)}` : undefined;
  const rep = repo ? { repo } : {};
  if (/^create_pull_request$/.test(op)) {
    const number = asNum(data.number);
    return number === undefined ? [] : [{ kind: 'pr-open', number, ...rep, ...(asStr(data.html_url) ? { url: String(data.html_url) } : {}) }];
  }
  if (/^merge_pull_request$/.test(op)) {
    if (data.merged === false) return [];
    const number = asNum(input.pull_number) ?? asNum(input.pullNumber);
    return [{ kind: 'pr-merge', ...(number !== undefined ? { number } : {}), ...rep }];
  }
  if (/^(?:create|publish)_release$/.test(op)) {
    const tag = asStr(input.tag_name) ?? asStr(data.tag_name);
    return [{ kind: 'release', ...(tag ? { tag } : {}), ...rep }];
  }
  if (/^(?:push_files|create_or_update_file|delete_file)$/.test(op)) {
    const branch = asStr(input.branch);
    return [{ kind: 'push', ...(branch ? { branch } : {}), ...rep }];
  }
  if (/(?:workflow_run|check_run|pull_request_status|commit_status|job)/.test(op)) {
    const state = (asStr(data.conclusion) ?? asStr(data.state) ?? '').toLowerCase();
    if (/^(?:failure|failed|error|timed_out|cancelled)$/.test(state)) return [{ kind: 'ci-fail', ...rep }];
    if (state === 'success') return [{ kind: 'ci-pass', ...rep }];
  }
  return [];
}

// ------------------------------------------------------------------ repetição

/** Mesmo evento do mesmo agente em poucos segundos (`gh pr checks` repetido) vale uma vez. */
export class GithubDedupe {
  private readonly seen = new Map<string, number>();
  constructor(private readonly windowMs = 90_000) {}

  /** true se é novo (e passa a contar). `at` em epoch ms. */
  fresh(agentId: string, signal: GithubSignal, at: number): boolean {
    const key = `${agentId}|${signal.kind}|${signal.number ?? ''}|${signal.branch ?? ''}|${signal.tag ?? ''}`;
    const before = this.seen.get(key);
    if (before !== undefined && Math.abs(at - before) < this.windowMs) return false;
    this.seen.set(key, at);
    if (this.seen.size > 200) {
      for (const [k, v] of this.seen) if (at - v > this.windowMs) this.seen.delete(k);
    }
    return true;
  }
}

/** Marca de histórico no detalhe da Activity: o evento já estava no transcript ao subir, não anima. */
export interface GithubDetail extends GithubSignal {
  hist?: boolean;
}

export function githubDetail(signal: GithubSignal, hist: boolean): string {
  return JSON.stringify(hist ? { ...signal, hist: true } : signal);
}

/** Lê o detalhe de uma Activity kind 'github' (undefined se estiver torto). */
export function parseGithubDetail(detail: string | undefined): GithubDetail | undefined {
  if (!detail) return undefined;
  const v = parseJson(detail);
  if (!isGithubKind(v.kind)) return undefined;
  return {
    kind: v.kind,
    ...(asStr(v.repo) ? { repo: String(v.repo) } : {}),
    ...(asNum(v.number) !== undefined ? { number: asNum(v.number) } : {}),
    ...(asStr(v.branch) ? { branch: String(v.branch) } : {}),
    ...(asStr(v.tag) ? { tag: String(v.tag) } : {}),
    ...(asStr(v.url) ? { url: String(v.url) } : {}),
    ...(v.hist === true ? { hist: true } : {}),
  };
}
